// Id → entity maps shared by pages. The lists are small (hundreds to a few
// thousand rows) and cached by TanStack Query, so every page can afford them.
import { useMemo } from "react";

import {
  useGetMe, useListLinks, useListMonitorStates, useListMonitors, useListNodes, useListProbes,
  type Link, type Monitor, type MonitorState, type Node, type Probe, type Role,
} from "@/api/generated";

function byId<T extends { id: string }>(list?: T[]): Map<string, T> {
  return new Map((list ?? []).map((x) => [x.id, x]));
}

export function useMe() {
  return useGetMe({ query: { staleTime: Infinity, retry: false } });
}

export function useIsAdmin(): boolean {
  return useMe().data?.role === "ADMIN";
}

export function useRole(): Role | undefined {
  return useMe().data?.role;
}

/** ADMIN or NOC: the people who run the network (customers see a subset). */
export const isInternalRole = (r?: Role) => r === "ADMIN" || r === "NOC";

export function useIsInternal(): boolean {
  return isInternalRole(useRole());
}

export function useIsCustomer(): boolean {
  return useRole() === "CUSTOMER";
}

export function useMonitorMap(): Map<string, Monitor> {
  const q = useListMonitors();
  return useMemo(() => byId(q.data), [q.data]);
}

// Probes and nodes are internal (docs/adr/2026-09-28-customer-tenancy.md):
// for customers these maps stay empty instead of asking and getting 403.
export function useProbeMap(): Map<string, Probe> {
  const q = useListProbes(undefined, { query: { enabled: useIsInternal() } });
  return useMemo(() => byId(q.data), [q.data]);
}

export function useNodeMap(): Map<string, Node> {
  const q = useListNodes(undefined, { query: { enabled: useIsInternal() } });
  return useMemo(() => byId(q.data), [q.data]);
}

/** "Probe 3" style names for customers, who never learn which probe is which. */
export function probeLabel(probes: Map<string, Probe>, id: string, all: string[]): string {
  const p = probes.get(id);
  if (p) return p.name;
  const i = [...all].sort().indexOf(id);
  return `Probe ${i >= 0 ? i + 1 : "?"}`;
}

export function useLinkMap(): Map<string, Link> {
  const q = useListLinks();
  return useMemo(() => byId(q.data), [q.data]);
}

export function useStateMap(): Map<string, MonitorState> {
  const q = useListMonitorStates();
  return useMemo(() => new Map((q.data ?? []).map((s) => [s.monitor_id, s])), [q.data]);
}

/** Root-first ancestry of a node (cycle-safe). */
export function nodePath(nodes: Map<string, Node>, id?: string | null): Node[] {
  const out: Node[] = [];
  const seen = new Set<string>();
  let cur = id ? nodes.get(id) : undefined;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur);
    cur = cur.parent_id ? nodes.get(cur.parent_id) : undefined;
  }
  return out;
}

/** Where a monitor sits: its link, or its node path. */
export function monitorPlace(m: Monitor | undefined, links: Map<string, Link>, nodes: Map<string, Node>): string {
  if (!m) return "";
  if (m.link_id) return links.get(m.link_id)?.name ?? "link";
  if (m.node_id) return nodePath(nodes, m.node_id).map((n) => n.name).join(" › ");
  return "";
}

export function monitorTarget(m: Monitor): string {
  if (m.check_type === "HTTP") return String(m.check_config.url ?? m.target_host);
  return m.target_port ? `${m.target_host}:${m.target_port}` : m.target_host;
}
